import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { environment } from 'src/environments/environment';
import { CheckboxComponent } from 'src/app/shared/ui-common/checkbox/checkbox.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { SharedService } from 'src/app/shared/data/shared.service';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { TranslationService } from 'src/app/shared/data/translation.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SettingsService, UnsubscribeModel } from '../data/settings.service';
import { ButtonComponent } from 'src/app/shared/ui-common/button/button.component';
import { SelectComponent, SelectOption } from 'src/app/shared/ui-common/select/select.component';
import { MarkdownPipe } from 'src/app/shared/data/pipes/markdown.pipe';

enum UnsubscribeResult {
  Success = 'success',
  Error = 'error',
}

@Component({
  selector: 'app-feature-unsubscribe',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CheckboxComponent,
    TranslatePipe,
    RouterModule,
    ButtonComponent,
    SelectComponent,
    MarkdownPipe
  ],
  templateUrl: './feature-unsubscribe.component.html',
  styleUrl: './feature-unsubscribe.component.scss',
})
export class FeatureUnsubscribeComponent implements OnInit, OnDestroy {

  private formBuilder = inject(FormBuilder);
  private settingService = inject(SettingsService);
  private formValueSubscription?: Subscription;
  private danishLanguageId = '1041';
  private token = '';

  labels = signal<Map<string, string>>(new Map());
  isManagerUnsubscribe = false;
  unsubscribe?: UnsubscribeModel;
  s3BaseUrl = environment.s3BaseUrl;
  companyLogo = signal<string>('');
  showForm = true;
  isSubmitting = false;
  unsubscribeResult: UnsubscribeResult | null = null;

  readonly UnsubscribeResult = UnsubscribeResult;
  selectOptions: SelectOption[] = [
    { label: 'App.v2.Views.Unsubscribe.Reason.DontWantNotifications', value: 'DontWantNotifications' },
    { label: 'App.v2.Views.Unsubscribe.Reason.NeverSignedUp', value: 'NeverSignedUp' },
    { label: 'App.v2.Views.Unsubscribe.Reason.Other', value: 'Other' },
    { label: 'App.v2.Views.Unsubscribe.Reason.Spam', value: 'Spam' },
    { label: 'App.v2.Views.Unsubscribe.Reason.TooManyNotifications', value: 'TooManyNotifications' },
  ];
  form: FormGroup = this.formBuilder.group({
    notifyBySMS: [{ value: true, disabled: true }],
    notifyByEmail: [{ value: true, disabled: true }],
    notifyByPush: [{ value: true, disabled: true }],
    reason: ['', Validators.required],
  });
  showConsent = false;

  get isDisabled(): boolean {
    return this.isSubmitting || (!this.showForm && !this.form.get('reason')?.valid);
  }

  get firstname(): string {
    return this.unsubscribe?.firstname ?? '';
  }

  get lastname(): string {
    return this.unsubscribe?.lastname ?? '';
  }

  get nextPushNotificationDate(): Date | null {
    return this.unsubscribe?.nextPushNotificationDate
      ? new Date(this.unsubscribe?.nextPushNotificationDate)
      : null;
  }

  get nextSMSNotificationDate(): Date | null {
    return this.unsubscribe?.nextSMSNotificationDate
      ? new Date(this.unsubscribe?.nextSMSNotificationDate)
      : null;
  }

  get nextEmailNotificationDate(): Date | null {
    return this.unsubscribe?.nextEmailNotificationDate
      ? new Date(this.unsubscribe?.nextEmailNotificationDate)
      : null;
  }

  get isHowdyLogo(): boolean {
    return this.companyLogo().includes('HRONWellbeing');
  }

  constructor(
    private sharedService: SharedService,
    private translationService: TranslationService,
    private location: Location,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  async ngOnInit(): Promise<void> {
    this.isManagerUnsubscribe = this.router.url.includes('manager-unsubscribe') || this.router.url.includes('Web/ManagerUnsubscribe');
    this.companyLogo.set(this.sharedService.getCompanyLogoUrl(true));

    if (this.router.url.includes('Web/Unsubscribe')) {
      this.token = this.route.snapshot.queryParams['token'] ?? '';
    } else if (this.router.url.includes('Web/ManagerUnsubscribe')) {
      this.token = this.route.snapshot.queryParams['invitationCode'] ?? '';
    } else {
      this.token = this.route.snapshot.paramMap.get('token') ?? '';
    }

    if (this.isManagerUnsubscribe) {
      await this.getManagerUnsubscribeData();
    } else {
      await this.getUnsubscribeData();
    }

    this.formValueSubscription = this.form.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.onSubmit();
      });
  }

  async getUnsubscribeData(): Promise<void> {
    this.unsubscribe = await this.settingService.getUnsubscribe(this.token);

    if (this.unsubscribe) {
      const labelsMap = this.convertLabelsToMap(this.unsubscribe?.labels);
      this.labels.set(labelsMap);
      this.translationService.setTranslations(labelsMap);
      this.setFormValues();
    }
  }

  async getManagerUnsubscribeData(): Promise<void> {
    this.unsubscribe = await this.settingService.getManagerUnsubscribe(this.token);

    if (this.unsubscribe) {
      const labelsMap = this.convertLabelsToMap(this.unsubscribe?.labels);
      this.labels.set(labelsMap);
      this.translationService.setTranslations(labelsMap);
      this.setManagerFormValues();
    }
  }

  ngOnDestroy(): void {
    this.formValueSubscription?.unsubscribe();
  }

  getHowdyLogo(): void {
    this.companyLogo.set(this.sharedService.getHowdyLogoUrl(true));
  }

  goBack(): void {
    this.location.back();
  }

  onSubmit(): void {
    if (!this.form.dirty || !this.showForm || this.unsubscribeResult) {
      return;
    }

    if (this.isManagerUnsubscribe) {
      this.settingService.updateManagerUnsubscribe(this.form.value, this.token).subscribe({
        next: response => {
          if (this.unsubscribe) {
            this.unsubscribe.notifyByEmail = response.notifyByEmail;
          }
        }
      });
    } else {
      this.settingService.updateUnsubscribeSettings(this.form.value, this.token).subscribe({
        next: x => {
          if (this.unsubscribe) {
            this.unsubscribe.nextEmailNotificationDate = x.nextEmailNotificationDate ?? null;
            this.unsubscribe.nextPushNotificationDate = x.nextPushNotificationDate ?? null;
            this.unsubscribe.nextSMSNotificationDate = x.nextSMSNotificationDate ?? null;
          }
        }
      });
    }
  }

  onUnsubscribeClick(): void {
    if (this.unsubscribeResult) {
      return;
    }

    if (this.showForm) {
      this.showForm = false;
    } else {
      const reason = this.form.get('reason')?.value;
      if (!reason || this.form.get('reason')?.invalid || this.isSubmitting) {
        this.form.get('reason')?.markAsTouched();

        return;
      }

      this.isSubmitting = true;
      this.settingService.updateUnsubscribe(this.token, reason).subscribe({
        next: () => {
          this.form.get('reason')?.reset('', { emitEvent: false });
          this.unsubscribeResult = UnsubscribeResult.Success;
        },
        error: () => {
          this.unsubscribeResult = UnsubscribeResult.Error;
          this.isSubmitting = false;
        },
        complete: () => {
          this.isSubmitting = false;
        }
      });
    }
  }

  resetUnsubscribeFlow(): void {
    this.unsubscribeResult = null;
    this.showForm = false;
  }

  resendTermsOfService(): void {
    this.settingService.resendTos(this.token).subscribe(() => {
      this.router.navigate(['/login']);
    });
  }

  recallTermsOfService(): void {
    this.settingService.recallTos(this.token).subscribe(() => {
      this.showConsent = false;
    });
  }

  private convertLabelsToMap(labels: { [key: string]: string } | Map<string, string> | null | undefined): Map<string, string> {
    if (!labels) {
      return new Map();
    }

    if (labels instanceof Map) {
      return labels;
    }

    if (typeof labels === 'object' && labels !== null) {
      return new Map(Object.entries(labels));
    }

    return new Map();
  }

  private setManagerFormValues(): void {
    this.form.patchValue({
      notifyByEmail: this.unsubscribe?.notifyByEmail ?? false,
    });

    this.form.enable();
  }

  private setFormValues(): void {
    this.form.patchValue({
      notifyBySMS: this.unsubscribe?.notifyBySMS ?? false,
      notifyByEmail: this.unsubscribe?.notifyByEmail ?? false,
      notifyByPush: this.unsubscribe?.notifyByPush ?? false,
      userLanguage: this.unsubscribe?.languageId
        ? `${this.unsubscribe?.languageId}`
        : this.danishLanguageId,
    });

    if (this.unsubscribe?.notifyBySMSEnabled) {
      this.form.get('notifyBySMS')?.enable();
    }

    if (this.unsubscribe?.notifyByEmailEnabled) {
      this.form.get('notifyByEmail')?.enable();
    }

    if (this.unsubscribe?.notifyByPushEnabled) {
      this.form.get('notifyByPush')?.enable();
    }
  }
}
